<script lang="ts" setup>
import { OrderTableContentViewModel } from './orderTableContentViewModel';
import { OrderHeaderKey, TableContentProperty } from '../../interface/tableContentHeaders';
import TableCell from '../TableCell.vue';
import OrderAlert from './OrderAlert.vue';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { i18n } from '@/i18n';

import OrderStatusBadge from '@/corePagesModule/components/OrderStatusBadge.vue';
import { TableContentItem } from '../../interface/tableContentItems';
import { EditableCell } from '../../interface/uiState';
import { isEditableCell } from '../../interface/uiState';
import EditableTableCell from '../EditableTableCell.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import OrderStatusReviseModal from '@/corePagesModule/components/OrderStatusReviseModal.vue';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { isOrder } from '@/coreDomainsModule/entities/order';
import { OrderService } from '@/orderTableModule/domains/service/orderService';
import { useReminderMailIcon } from '@/orderTableModule/composables/useReminderMailIcon';
import TableMemoButton from '@/corePagesModule/tableMemoButton/TableMemoButton.vue';

const t = i18n.t;

const router = useRouter();

// Composables
const { getReminderMailIconName } = useReminderMailIcon();

// props
const props = withDefaults(
  defineProps<{
    header: TableContentProperty;
    orderHeaderKey: OrderHeaderKey;
    item: TableContentItem;
    headerIndex: number;
    itemIndex: number;
    accumulationTableContentWidth: number;
    isVariableWidth: boolean;
    isAllSelected: boolean;
    stableColumnCount: number;
    isBold: boolean;
    isAlertOpening: boolean;
    editingId: string;
    isPartialDelivery?: boolean;
    isChecked: boolean;
    isCheckedBusinessId: boolean;
    isOrderGroupEditMode?: boolean;
  }>(),
  {}
);

// emits
const emits = defineEmits<{
  (e: 'changeEditableValue', headerKey: OrderHeaderKey, value: string): void;
  (e: 'onClickNarrow'): void;
  (e: 'clickAlert'): void;
  (e: 'onClickEdit', id: string): void;
  (e: 'onClickSave', id: string): void;
  (e: 'onClickDelete', id: string): void;
  (e: 'onClickSaveMemo', id: string, memoText: string): void;
  (e: 'onClickDeleteMemo', id: string): void;
  (e: 'onClickReminderMail', id: string): void;
  (e: 'onClickOrderBasis', id: string): void;
}>();

const orderTableContentVM = new OrderTableContentViewModel();

const orderReviseModalState = ref(false);

const changedOrderStatus = ref('');

/**
 * 編集中のセルかどうかを判定する
 * @param headerKey - ヘッダーキー
 */
const isEditing = (headerKey: OrderHeaderKey): headerKey is EditableCell => {
  return props.editingId === props.item.orderId && props.header.isEditable && isEditableCell(props.orderHeaderKey);
};

/**
 * 編集可能かどうかを判定する
 * @param headerKey - ヘッダーキー
 * @param item - テーブルのアイテム
 */
const isEditable = (headerKey: OrderHeaderKey, item: TableContentItem): boolean => {
  if (headerKey === 'OrderEstimateTimeOfArrival') {
    return OrderService.canChangeOrderDeliveryDate(item);
  }
  if (headerKey === 'OrderAmount') {
    return OrderService.canChangeOrderAmount(item);
  }
  return true;
};

/**
 * 編集状態でステータスを変更した際の処理
 * @param orderStatusString - ステータスの文字列
 */
const onConfirmedEditStatus = (orderStatusString: string) => {
  changedOrderStatus.value = orderStatusString;
  orderReviseModalState.value = false;
  emits('changeEditableValue', props.orderHeaderKey, orderStatusString);
};

const textAlign = computed(() => {
  return props.header.propertyPath?.propertyType === 'number' ? 'right' : 'left';
});

/**
 * アラート内の確認ボタンをクリックした際の処理
 * @param alertKind - アラートの種類
 */
const onClickCheckButtonInAlert = (alertKind: string) => {
  if (alertKind === 'orderAmountWarning') {
    // 詳細ページに移動する
    window.open(`#/business/recommend-order/${props.item.aggregateBusinessId}`, '_blank');
  }
};

/**
 * itemが発注か分納か判定して、displayOrderItemかdisplayPartialDeliveryItemを呼び出す
 * @param orderHeaderKey - ヘッダーキー
 * @param item - テーブルのアイテム
 */
const displayOrderItem = (orderHeaderKey: OrderHeaderKey, item: TableContentItem) => {
  return props.isPartialDelivery
    ? orderTableContentVM.displayPartialDeliveryItem(orderHeaderKey, item)
    : orderTableContentVM.displayOrderItem(orderHeaderKey, item);
};

/**
 * 保存ボタンをDisabledにするかどうか
 */
const isDisabledSaveButton = () => {
  if (isOrder(props.item)) {
    return !OrderService.checkOrderIntegrity(props.item);
  }
  return false;
};

/**
 * メモの表示内容を取得する
 * @param item - テーブルのアイテム
 */
const getMemoValue = (item: TableContentItem) => {
  const value = orderTableContentVM.displayOrderItem('OrderMemo', item);
  return value === '' ? undefined : value;
};

const isPaddingAvailCond = computed(() => {
  switch (props.header.key) {
    case 'OrderEditButton':
      return props.isOrderGroupEditMode;
    case 'OrderDeleteButton':
      return !props.editingId;
    default:
      return false;
  }
});
</script>

<template>
  <TableCell
    :class="[
      orderTableContentVM.isSticky(stableColumnCount, headerIndex) ? 'sticky-content' : '',
      orderHeaderKey === 'OrderDeleteButton' || isPaddingAvailCond ? 'no-padding' : '',
    ]"
    :style="{
      left: orderTableContentVM.calcStickyDiff(stableColumnCount, headerIndex, accumulationTableContentWidth),
    }"
    class="table-cell base-table__body--fixed"
    :text-align="textAlign"
    :table-content-property="header"
    :tool-tip-value="orderHeaderKey === 'OrderRecommendStatus' ? undefined : displayOrderItem(orderHeaderKey, item)"
    :is-highlighted="props.isChecked || props.isCheckedBusinessId"
  >
    <template v-if="orderHeaderKey === 'OrderRecommendStatus'">
      <div
        class="order-recommend-status"
        :class="{ 'order-recommend-status--editable': isEditing(orderHeaderKey) }"
        @click="isEditing(orderHeaderKey) ? (orderReviseModalState = true) : () => {}"
      >
        <OrderStatusBadge :order-status-str="displayOrderItem(orderHeaderKey, item)" />
        <SvgLoader v-if="isEditing(orderHeaderKey)" name="arrowDown" />
      </div>
    </template>
    <template v-else-if="orderHeaderKey === 'OrderMemo'">
      <TableMemoButton
        :memo="getMemoValue(item)"
        @save="
          (memoText) => {
            emits('onClickSaveMemo', item.orderId, memoText);
          }
        "
        @delete="
          () => {
            emits('onClickDeleteMemo', item.orderId);
          }
        "
      />
    </template>
    <template v-else-if="orderHeaderKey === 'OrderAlert'">
      <!-- アラートアイコン設置部分 -->
      <OrderAlert
        v-bind="{ isOpening: props.isAlertOpening, ...orderTableContentVM.displayOrderAlert(item) }"
        @click="
          (isOpening) => {
            if (isOpening) emits('clickAlert');
          }
        "
        @on-next="onClickCheckButtonInAlert"
      />
    </template>
    <template v-else-if="orderHeaderKey === 'OrderEditButton'">
      <template v-if="!props.isPartialDelivery && !props.isOrderGroupEditMode">
        <template v-if="isEditing(orderHeaderKey)">
          <TextButton
            button-style="btn-secondary"
            size="btn-ss"
            :disabled="isDisabledSaveButton()"
            @click="() => emits('onClickSave', item.orderId)"
            >{{ t('保存') }}</TextButton
          >
        </template>
        <template v-else-if="props.editingId === ''">
          <TextButton button-style="btn-secondary" size="btn-ss" @click="() => emits('onClickEdit', item.orderId)">{{
            t('編集')
          }}</TextButton>
        </template>
      </template>
    </template>
    <template v-else-if="orderHeaderKey === 'ReminderMail'">
      <template v-if="!props.isPartialDelivery">
        <button class="reminder-mail-button" @click="() => emits('onClickReminderMail', item.orderId)">
          <SvgLoader :name="getReminderMailIconName(item.reminderMailDataId)" class="reminder-mail-icon" />
        </button>
      </template>
    </template>
    <template v-else-if="orderHeaderKey === 'OrderDeleteButton'">
      <template v-if="isEditing(orderHeaderKey) && isPartialDelivery">
        <button class="right-align" @click="emits('onClickDelete', item.id)"><SvgLoader name="closeButtonIcon" /></button>
      </template>
    </template>
    <template v-else-if="orderHeaderKey === 'OrderBasis'">
      <button v-if="item.orderBasisId" class="order-basis-button" @click="emits('onClickOrderBasis', item.orderId)">
        <SvgLoader name="tableView" />
      </button>
    </template>
    <template v-else>
      <span v-if="isEditing(orderHeaderKey)">
        <EditableTableCell
          v-if="isEditable(orderHeaderKey, item)"
          :input-type="header.propertyPath?.propertyType"
          :text-value="orderHeaderKey === 'OrderPortEta' && item.portEta == null ? '' : displayOrderItem(orderHeaderKey, item)"
          @change-date-value="emits('changeEditableValue', orderHeaderKey, DateService.convertIDateToDate($event).toISOString())"
          @change-text="emits('changeEditableValue', orderHeaderKey, $event)"
        />
        <span v-else>{{ displayOrderItem(orderHeaderKey, item) }}</span>
      </span>
      <span v-else class="table-cell-content" :class="{ 'table-cell-content': isBold }" @click="emits('onClickNarrow')">
        {{ displayOrderItem(orderHeaderKey, item) }}
      </span>
    </template>
  </TableCell>
  <OrderStatusReviseModal
    :modal-state="orderReviseModalState"
    :header-title="t('ステータスの変更')"
    :description="t('発注のステータスを変更することができます。')"
    :items="[item]"
    :is-partial-delivery="props.isPartialDelivery"
    @canceled="() => (orderReviseModalState = false)"
    @confirm="onConfirmedEditStatus($event.toString())"
  />
</template>

<style lang="scss" scoped>
.table-cell-content {
  font-family: Noto Sans JP;
  font-size: $fontSizeSS;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &--bold {
    font-weight: bold;
  }
}

.order-recommend-status {
  display: grid;
  grid-template-columns: repeat(2, auto);
  gap: 4px;
  align-items: center;

  &--editable:hover {
    cursor: pointer;
    opacity: 0.8;
    transition: opacity 0.3s;
  }
}

.no-padding {
  padding: 0;
}

.right-align {
  display: grid;
  justify-content: end;
}

.reminder-mail-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: grid;
  place-items: center;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
    transition: background-color 0.2s;
  }
}

.reminder-mail-icon {
  width: 20px;
  height: 20px;
}
</style>
